import React, {useCallback} from 'react';
import {Bar, Line} from "react-chartjs-2";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip
} from 'chart.js';
import {makeSummaryChartData, makeSummaryChartLineOptions, makeSummaryChartOptions} from "./helper";
import {activityStore} from "../../../../../store/activity.store";
import {themeStore} from "../../../../../store/theme.store";
import { Segmented } from "../../../../ui/segmented";
import {GroupBy, TrendChartType} from "../../../../../enum/activity.enum";
import { BarChart3, LineChart } from "lucide-react";
import { Checkbox } from "../../../../ui/checkbox";
import {useLocalStorage} from "react-use";
import {Settings} from "../../../../../model/auth.model";
import {APP_SETTINGS_LS_KEY} from "../../../../../constant/key";
import {trackEvent} from "../../../../../utils/analytics";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  PointElement,
  LineElement, Filler
);

const groupOptions  = [
  { label: 'Day', value:'day' }, { label: 'Week', value:'week' }, { label: 'Month', value:'month' }
]

const chartOptions  = [
  { value: TrendChartType.BAR, icon: <BarChart3 className="h-4 w-4" /> }, { value: TrendChartType.LINE, icon: <LineChart className="h-4 w-4" />}
]

const ActivityBars = () => {
  const groupBy = activityStore(state => state.intervalsParams.groupBy);
  const statistics = activityStore(state => state.statistics);
  const setIntervalsParams = activityStore(state => state.setIntervalsParams);
  const isDark = themeStore(state => state.isDark);

  const onGroupByChange = useCallback((value: string | number) => {
    trackEvent('grouping_change');
    setIntervalsParams({ groupBy: value as GroupBy });
  }, [setIntervalsParams]);

  const [settings, setSettings] = useLocalStorage<Settings | null>(
    APP_SETTINGS_LS_KEY,
    null
    // ,{ raw: true }
  );

  const onGroupedChange = useCallback((checked: boolean) => {
    setSettings((prevSettings) => ({
      ...prevSettings,
      displayGroupsInBarStatistics: checked,
      trendChartType: prevSettings?.trendChartType ?? TrendChartType.BAR,
      displayTypesFullPath: prevSettings?.displayTypesFullPath ?? false, // Ensure the property is always defined
    }));
  }, [setSettings]);

  const onTrendChartTypeChange = useCallback((value: string | number) => {
    setSettings((prevSettings) => ({
      ...prevSettings,
      displayGroupsInBarStatistics: prevSettings?.displayGroupsInBarStatistics ?? false,
      trendChartType: value as TrendChartType,
      displayTypesFullPath: prevSettings?.displayTypesFullPath ?? false, // Ensure the property is always defined
    }));
  }, [setSettings]);

  if (!statistics) {
    return null
  }

  const data = makeSummaryChartData(statistics, settings?.displayGroupsInBarStatistics ?? false);
  const bar = settings?.trendChartType === TrendChartType.BAR
  // Disable animation in E2E tests for consistent screenshots
  const isE2ETest = typeof window !== 'undefined' && localStorage.getItem('e2eTest') === 'true';
  const animated = !isE2ETest;
  const options = makeSummaryChartOptions(animated, isDark);
  const options2 = makeSummaryChartLineOptions(animated, isDark);

  return <div>
    <div className="flex items-center gap-4 flex-wrap">
      <label className="flex items-center gap-2 cursor-pointer">
        <Checkbox
          checked={settings?.displayGroupsInBarStatistics || false}
          onCheckedChange={onGroupedChange}
        />
        <span className="text-sm">Grouped</span>
      </label>

      <div className="flex items-center gap-2">
        <span className="text-sm">Granularity:</span>
        <Segmented options={groupOptions} value={groupBy} onChange={onGroupByChange}/>
      </div>

      <Segmented options={chartOptions} value={settings?.trendChartType ?? TrendChartType.BAR}
                 onChange={onTrendChartTypeChange} />
    </div>

    { bar ?
      <div className="mt-5 w-full">
          <Bar
            style={{ minHeight: 250, maxHeight: 350, minWidth: '100%' }}
            options={options}
            data={data}
          />
      </div>

    :
      <div className="mt-5 w-full">
        <Line
          style={{ minHeight: 250, maxHeight: 350, minWidth: '100%' }}
          options={options2} data={data}/>
      </div>
    }
  </div>
}

export default ActivityBars;
